import React, { useState } from "react"
import { makeStyles, Typography } from "@material-ui/core"
import { Logger } from "shared/Helpers/logging"
import { StandardLoadingButton } from "shared/Components/Loading/LoadingButton"
import { LocalizedStrict } from "shared/Modules/Localization/Components/Localized"
import { ErrorDisplay } from "shared/Modules/Error/errorTypes"
import { PushTypeToggles } from "shared/Modules/Notification/Components/PushTypeToggles"
import { DEFAULT_PUSH_NOTIFICATION_TYPES, PushNotificationTypes, usePushPermission } from "shared/Modules/Notification/usePushPermission"
import { OnboardingDrawer } from "./OnboardingDrawer"
import { ONBOARDING_STEP_COUNT, PushPermissionResult } from "../onboardingTypes"

const useStyles = makeStyles((theme) => ({
    content: {
        padding: theme.spacing(0, 2, 2),
    },
    footnote: {
        display: "block",
        textAlign: "center",
        marginBottom: theme.spacing(1.5),
    },
}))

type Props = Readonly<{
    onDone: (result: PushPermissionResult) => void
    // Go back to the location step (only offered when that step was shown)
    onBack?: () => void
}>

/**
 * Step 2: choose which push notifications to receive and allow them in the OS. Pressing continue
 * shows the OS permission dialog; the answer and the chosen types are saved as user settings.
 * Skipping saves nothing, so the user is asked again on the next login.
 */
export function PushPermissionStep({ onDone, onBack }: Props) {
    const classes = useStyles()
    const logger = new Logger("onboarding")

    const [types, setTypes] = useState<PushNotificationTypes>(DEFAULT_PUSH_NOTIFICATION_TYPES)
    const { requestPermission, requesting, saving } = usePushPermission({ errorDisplay: ErrorDisplay.Dialog })
    const inProgress = requesting || saving

    function handleContinue() {
        requestPermission(types)
            .then((granted) => {
                logger.info(`Push permission step finished [granted: ${granted}]`)
                onDone({ granted, ...types })
            })
    }

    function handleSkip() {
        logger.info("Push permission step skipped")
        onDone({ granted: undefined, ...types })
    }

    return (
        <OnboardingDrawer
            name="push-permission"
            step={2}
            totalSteps={ONBOARDING_STEP_COUNT}
            onBack={inProgress || !onBack ? undefined : onBack}
            onSkip={inProgress ? undefined : handleSkip}
            title={<LocalizedStrict id="onboarding-push-title"><span>Må vi sende dig pushbeskeder?</span></LocalizedStrict>}
            description={
                <LocalizedStrict id="onboarding-push-description">
                    <span>
                        Så får du besked når din ordre er bekræftet og klar til afhentning, hvis noget ændrer sig i din ordre
                        eller booking, og når et møde nærmer sig. Beskeder fra kantinen kommer kun fra den kantine du har valgt.
                    </span>
                </LocalizedStrict>
            }
            actions={
                <>
                    <LocalizedStrict id="onboarding-push-footnote">
                        <Typography variant="caption" color="textSecondary" className={classes.footnote}>
                            Ændres under Mig › Indstillinger › Pushbeskeder
                        </Typography>
                    </LocalizedStrict>
                    <LocalizedStrict id="onboarding-push-continue-button" attrs={{ loadingLabel: true }}>
                        <StandardLoadingButton
                            variant="contained"
                            color="black"
                            size="large"
                            fullWidth
                            loading={inProgress}
                            loadingLabel="Gemmer..."
                            onClick={handleContinue}
                        >
                            Fortsæt
                        </StandardLoadingButton>
                    </LocalizedStrict>
                </>
            }
        >
            <PushTypeToggles
                value={types}
                disabled={inProgress}
                onChange={(type, checked) => setTypes((prev) => ({ ...prev, [type]: checked }))}
                className={classes.content}
            />
        </OnboardingDrawer>
    )
}
